import React, { useState, useContext, createContext, ReactNode, Dispatch, SetStateAction, useEffect } from 'react';
import { MediaItem } from './interfaces';
import { useNavigate, useParams } from 'react-router-dom';
import { ResolvePath } from 'features/firebase-app';
import { getUserClaims } from 'features/application/application-slice';
import { get_content_carrier } from 'features/auto-thunks';
import { ContentCarrier } from 'models';
import { useSelector, useDispatch } from 'react-redux';
import { hasSubdomain, extractSubdomain } from './MainComponent';
import { get_all_contentCarrier } from 'features/contentCarrier/contentCarrier-slice';

// Define the shape of the context data
interface PlaywatchKidsContextType {
    showSide: boolean;
    searchTerm: string;
    contentCarrier: ContentCarrier;
    selectedCategory: string;
    focusFunc: (b: boolean) => void;
    setSelectedCategory: Dispatch<SetStateAction<string>>;
    setFocusFunc: (focusFunc: any) => void;
    selectedMedia: MediaItem;
    setSelectedMedia: (media: MediaItem) => void;
    search: (term: string) => void;
    searchFocused: boolean;
    setSearchFocused: (f: boolean) => void;
    clearMedia: () => void;
    clearSearchCallback: (callback: any) => void
}

// Create the context with a default value
const PlaywatchKidsContext = createContext<PlaywatchKidsContextType>({
    selectedCategory: '',
    showSide: false,
    searchTerm: '',
    contentCarrier: null,
    focusFunc: (b) => { },
    setSelectedCategory: () => { },
    setFocusFunc: (focusFunc) => { },
    selectedMedia: null,
    search: (term: string) => { },
    setSelectedMedia: (media: MediaItem) => { },
    searchFocused: false,
    setSearchFocused: (f: boolean) => { },
    clearMedia: () => { },
    clearSearchCallback: (callback: any) => { }
});

interface PlaywatchKidsProviderProps {
    children: ReactNode;
}

export const PlaywatchKidsProvider: React.FC<PlaywatchKidsProviderProps> = ({ children }) => {
    const [focusFunc, setFocusFunc] = useState(null);
    const [searchFocused, setSearchFocused] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState<MediaItem>(null);
    const [clearSearch, setClearSearch] = useState(null);
    const [showSide, setShowSide] = useState(false);
    const navigate = useNavigate()
    const params = useParams();
    const all_contentCarriers = useSelector(get_all_contentCarrier)
    const [contentCarrier, setContentCarrier] = useState<ContentCarrier>(all_contentCarriers[0]);
    const [selectedCategory, setSelectedCategory] = useState(all_contentCarriers?.[0]?.media?.categories?.[0]?.listId || '');
    const [searchTerm, setSearchTerm] = useState('');
    const claims = useSelector(getUserClaims);
    const dispatch = useDispatch();

    useEffect(() => {
        if (params?.id) {
            if (contentCarrier) {
                let mediaItem = getMediaByIdFromContentCarrier(contentCarrier, params?.id)
                if (mediaItem?.id === params?.id) {
                    setShowSide(true);
                    setSelectedMedia(mediaItem);
                }
            }
        }
        else {
            setShowSide(false)
        }
    }, [params?.id, contentCarrier]);

    useEffect(() => {
        document.body.classList.add('playwatch-kids')
        if (claims?.user) {
            const currentUrl = window.location.href;
            Promise.resolve().then(async () => {
                await dispatch(get_content_carrier({
                    userId: claims.user,
                    domain: hasSubdomain(currentUrl) ? extractSubdomain(currentUrl) : null,
                    callback: (success, res) => {
                        console.log(res);
                        setContentCarrier(res.data);
                        let categories = res.data?.media?.categories
                        if (categories?.length) {
                            setSelectedCategory(categories[0].listId);
                        }
                    }
                }));
            });
        }
        return () => {
            document.body.classList.remove('playwatch-kids')
        }
    }, [claims?.user]);

    return (
        <PlaywatchKidsContext.Provider value={{
            focusFunc,
            setFocusFunc,
            clearSearchCallback: (callback) => {
                setClearSearch(() => callback);
            },
            searchTerm,
            selectedCategory,
            setSelectedCategory: (category) => {
                navigate(ResolvePath(`/Home/`));
                setSelectedMedia(null);
                setSearchTerm('');
                setSelectedCategory(category);
            },
            selectedMedia,
            setSelectedMedia: (value) => {
                setSelectedMedia(value);
                setSearchTerm('');
                navigate(ResolvePath(`/Home/${value.id}`))
            },
            searchFocused,
            setSearchFocused,
            showSide,
            contentCarrier,
            search: (term) => {
                setSearchTerm(term);
                navigate(ResolvePath(`/Home/`));
                setSelectedMedia(null);
                if (clearSearch) {
                    clearSearch()
                }
            },
            clearMedia: () => {
                setSelectedMedia(null);
                setSearchTerm('');
                navigate(ResolvePath(`/Home/`));
                let categories = contentCarrier?.media?.categories;
                if (categories?.length) {
                    setSelectedCategory(categories[0].listId);
                }
            }
        }}>
            {children}
        </PlaywatchKidsContext.Provider >
    );
};

export const usePlaywatchKids = (): PlaywatchKidsContextType => {
    const context = useContext(PlaywatchKidsContext);
    if (context === undefined) {
        throw new Error('usePlaywatchKids must be used within a PlaywatchKidsProvider');
    }
    return context;
};



function getMediaByIdFromContentCarrier(contentCarrier: ContentCarrier, id: string): MediaItem | null {
    if (contentCarrier?.media?.categories) {
        for (let i = 0; i < contentCarrier?.media?.categories?.length; i++) {
            let category = contentCarrier?.media?.categories[i];
            if (category.media) {
                let item = category.media.find(x => x.id === id) || null;
                if (item) {
                    return item;
                }
            }
        }
    }
    return null;
}